import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BoltIcon,
  CheckIcon,
  CubeTransparentIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { marketingImages } from '@/assets/images/marketing/imageUrls';
import { toast } from 'react-hot-toast';
import {
  DEFAULT_COMPARE_ROWS,
  DEFAULT_WHO_CARDS,
  parseCompareRows,
  parseWhoCards,
} from '@/components/admin/content/homepageCmsTypes';

export interface WhyTMOSTab {
  id: string;
  tab_key: string;
  title: string;
  description?: string;
  content_html: string;
  display_order: number;
}

interface WhatIsTMOSContentProps {
  homepageContent: Record<string, string>;
  tabs?: WhyTMOSTab[];
  loading?: boolean;
  t: (key: string) => string;
  /** When true, show compact layout (e.g. for landing). */
  compact?: boolean;
  /** Renders when Try AI is opened (e.g. embedded planning chat). */
  tryAiPanel?: React.ReactNode;
  /** Controlled open state for Try AI panel; if omitted, component manages state. */
  tryAiOpen?: boolean;
  onTryAiOpenChange?: (open: boolean) => void;
}

/**
 * Public landing: Hero → Approach/How → Impact → CTA → Who.
 * All sections share the same full content width as the block rows.
 */
export const WhatIsTMOSContent: React.FC<WhatIsTMOSContentProps> = ({
  homepageContent,
  t,
  compact = false,
  tryAiPanel,
  tryAiOpen: tryAiOpenControlled,
  onTryAiOpenChange,
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [tryAiOpenInternal, setTryAiOpenInternal] = useState(false);
  const tryAiOpen = tryAiOpenControlled ?? tryAiOpenInternal;

  const setTryAiOpen = (open: boolean) => {
    if (onTryAiOpenChange) {
      onTryAiOpenChange(open);
    } else {
      setTryAiOpenInternal(open);
    }
  };

  // CMS (homepage_content) preferred; translations are fallback for empty / other languages
  const cms = (key: string, translationKey: string) => {
    const value = homepageContent[key]?.trim();
    return value || t(translationKey);
  };

  const headingClass = compact
    ? 'text-2xl md:text-3xl font-normal leading-tight text-[var(--color-primary-text)]'
    : 'text-3xl sm:text-4xl font-normal leading-tight text-[var(--color-primary-text)]';

  // One step below hero so Approach / How cards don't compete with the page title
  const solutionCardHeadingClass = compact
    ? 'text-xl md:text-2xl font-normal leading-tight'
    : 'text-2xl sm:text-3xl font-normal leading-tight';

  const handleGetStarted = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t('website.home.cta.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('website.home.cta.emailInvalid'));
      return;
    }
    navigate(`/register?email=${encodeURIComponent(trimmed)}`);
  };

  const handleTryAi = () => {
    setTryAiOpen(true);
    requestAnimationFrame(() => {
      document.getElementById('try-ai')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const orgCardsFromCms = parseWhoCards(homepageContent.who_cards);
  const orgCards = (orgCardsFromCms || DEFAULT_WHO_CARDS).map((card) => ({
    key: card.id,
    title: card.title,
    descLines: card.descLines,
    roles: card.roles,
    image: marketingImages.landingOrganization[card.imageKey],
  }));

  // Fallback org cards from translations when CMS who_cards missing (legacy keys)
  const orgCardsLegacy = [
    {
      key: 'peFirms',
      title: t('website.home.organization.peFirms'),
      descLines: [
        t('website.home.organization.peFirmsDesc1'),
        t('website.home.organization.peFirmsDesc2'),
      ],
      roles: t('website.home.organization.peFirmsRoles'),
      image: marketingImages.landingOrganization.peFirms,
    },
    {
      key: 'peBacked',
      title: t('website.home.organization.peBacked'),
      descLines: [
        t('website.home.organization.peBackedDesc1'),
        t('website.home.organization.peBackedDesc2'),
      ],
      roles: t('website.home.organization.peBackedRoles'),
      image: marketingImages.landingOrganization.peBacked,
    },
    {
      key: 'consultants',
      title: t('website.home.organization.consultantsLabel'),
      descLines: [
        t('website.home.organization.consultantsDesc1'),
        t('website.home.organization.consultantsDesc2'),
      ],
      roles: t('website.home.organization.consultantsRoles'),
      image: marketingImages.landingOrganization.consultants,
    },
  ];

  const whoCards = orgCardsFromCms ? orgCards : orgCardsLegacy;

  const impactValueProps = [
    {
      key: 'os',
      cmsKey: 'impact_card_1',
      statementKey: 'website.home.whyTmos.os',
      icon: CubeTransparentIcon,
    },
    {
      key: 'alignment',
      cmsKey: 'impact_card_2',
      statementKey: 'website.home.whyTmos.alignment',
      icon: LinkIcon,
    },
    {
      key: 'speed',
      cmsKey: 'impact_card_3',
      statementKey: 'website.home.whyTmos.speed',
      icon: BoltIcon,
    },
  ] as const;

  const compareRowsFromCms = parseCompareRows(homepageContent.compare_rows);
  const compareRows =
    compareRowsFromCms ||
    DEFAULT_COMPARE_ROWS.map((row) => ({
      label: row.label,
      others: row.others,
    }));

  // Legacy flat CMS/translation fallback when compare_rows JSON absent
  const compareRowsLegacy = [
    {
      label: cms('compare_row_multi_tenant', 'website.home.compare.row.multiTenant'),
      others: cms('compare_others_mostly_no', 'website.home.compare.others.mostlyNo'),
    },
    {
      label: cms('compare_row_self_service', 'website.home.compare.row.selfService'),
      others: cms('compare_others_sometimes', 'website.home.compare.others.sometimes'),
    },
    {
      label: cms('compare_row_ai_planning', 'website.home.compare.row.aiPlanning'),
      others: cms('compare_others_sometimes', 'website.home.compare.others.sometimes'),
    },
    {
      label: cms('compare_row_marketplace', 'website.home.compare.row.marketplace'),
      others: cms('compare_others_mostly_no', 'website.home.compare.others.mostlyNo'),
    },
    {
      label: cms('compare_row_full_os', 'website.home.compare.row.fullOs'),
      others: cms('compare_others_rare', 'website.home.compare.others.rare'),
    },
  ];

  const differentiatorRows = compareRowsFromCms ? compareRows : compareRowsLegacy;

  const approachTitle = cms('approach_title', 'website.home.solution.approachTitle');
  const howTitle = cms('how_title', 'website.home.solution.techTitle');
  const ctaEmailPlaceholder = cms('cta_email_placeholder', 'website.home.cta.emailPlaceholder');

  return (
    <div className={compact ? 'w-full space-y-10 pb-8' : 'w-full space-y-12 pb-12'}>
      {/* 1) Hero */}
      <section className="w-full text-center" aria-labelledby="landing-solution">
        <h1
          id="landing-solution"
          className={
            compact
              ? 'text-2xl md:text-3xl font-normal leading-tight mb-3 text-[var(--color-primary-text)]'
              : 'text-4xl sm:text-5xl font-normal leading-tight mb-4 text-[var(--color-primary-text)]'
          }
        >
          {cms('hero_title', 'website.home.hero.title')}
        </h1>
        <p
          className={
            compact
              ? 'w-full text-base text-[var(--color-surface-text)]'
              : 'w-full text-xl sm:text-2xl text-[var(--color-surface-text)]'
          }
        >
          {cms('hero_subtitle', 'website.home.hero.subtitle')}
        </p>
      </section>

      {/* 2) Our Approach + How we make it work — filled primary cards */}
      <section className="w-full" aria-label={approachTitle}>
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 text-left">
          {(
            [
              {
                key: 'approach',
                title: approachTitle,
                body: cms('approach_body', 'website.home.solution.approachBody'),
              },
              {
                key: 'how',
                title: howTitle,
                body: cms('how_body', 'website.home.solution.techBody'),
              },
            ] as const
          ).map(({ key, title, body }) => (
            <div
              key={key}
              className={`rounded-lg border p-4 shadow-sm ${
                key === 'approach' ? 'tmos-logo-bg-approach' : 'tmos-logo-bg-how'
              }`}
            >
              <h2 className={`${solutionCardHeadingClass} mb-2 text-[var(--color-logo-on-gradient)]`}>
                {title}
              </h2>
              <p className="text-sm leading-relaxed text-[var(--color-logo-on-gradient)] opacity-95">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 3) Impact */}
      <section
        className="w-full rounded-lg bg-[var(--color-secondary-bg)] border border-[var(--color-border)] p-4 sm:p-5 text-left"
        aria-labelledby="landing-impact-headline"
      >
        <span className="inline-block rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[var(--color-primary-text)]">
          {cms('impact_label', 'website.home.impact.label')}
        </span>
        <p
          id="landing-impact-headline"
          className={`mt-3 w-full ${solutionCardHeadingClass} text-[var(--color-surface-text)]`}
        >
          {cms('impact_headline', 'website.home.problem.statement')}
        </p>
        <div className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-4">
          {impactValueProps.map(({ key, cmsKey, statementKey, icon: Icon }) => (
            <div
              key={key}
              className="flex min-h-[7.5rem] flex-col items-start justify-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-5 text-left shadow-sm"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-secondary-bg)]">
                <Icon className="h-5 w-5 text-[var(--color-primary-text)]" aria-hidden />
              </div>
              <p className="leading-relaxed text-[var(--color-surface-text)]">
                {cms(cmsKey, statementKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full" aria-label={t('website.home.cta.ariaLabel')}>
        <form
          onSubmit={handleGetStarted}
          className="flex w-full flex-col sm:flex-row gap-2 sm:gap-3 items-stretch"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={ctaEmailPlaceholder}
            autoComplete="email"
            className="flex-1 min-h-[48px] px-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-tab-inactive-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-text)] focus:border-transparent"
            aria-label={ctaEmailPlaceholder}
          />
          <button
            type="submit"
            className="min-h-[48px] px-5 rounded-xl tmos-logo-bg-cta text-base font-semibold transition-opacity whitespace-nowrap"
          >
            {cms('cta_get_started', 'website.home.cta.getStarted')}
          </button>
          <button
            type="button"
            onClick={handleTryAi}
            className="min-h-[48px] px-5 rounded-xl border-2 border-[var(--color-primary-text)] bg-[var(--color-surface-bg)] text-[var(--color-primary-text)] text-base font-semibold hover:bg-[var(--color-secondary-bg)] transition-colors sm:whitespace-nowrap"
          >
            {cms('cta_try_ai', 'website.home.cta.tryAi')}
          </button>
        </form>
      </section>

      {tryAiOpen && tryAiPanel && (
        <section id="try-ai" className="scroll-mt-6">
          {tryAiPanel}
        </section>
      )}

      {/* Who */}
      <section aria-labelledby="landing-who">
        <div className="mb-4 text-center">
          <h2 id="landing-who" className={headingClass}>
            {cms('organization_title', 'website.home.organization.title')}
          </h2>
        </div>
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
          {whoCards.map((card) => (
            <div
              key={card.key}
              className="min-w-0 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="aspect-[16/10] w-full bg-[var(--color-secondary-bg)]">
                <img
                  src={card.image}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="text-lg font-medium text-[var(--color-surface-text)]">{card.title}</h3>
                <ul className="mt-2 list-inside space-y-0.5 text-sm text-[var(--color-tab-inactive-text)]">
                  {card.descLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-[var(--color-primary-text)]">
                  {card.roles}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How T-MOS is different */}
      <section aria-labelledby="landing-compare" className="w-full">
        <div className="mb-3 text-center">
          <h2 id="landing-compare" className={headingClass}>
            {cms('compare_title', 'website.home.compare.title')}
          </h2>
        </div>
        <div className="flex justify-center overflow-x-auto">
          <table className="w-auto border-collapse text-center text-sm">
            <thead>
              <tr>
                <th scope="col" className="px-4 py-2 font-semibold text-[var(--color-surface-text)] whitespace-nowrap">
                  <span className="sr-only">{t('website.home.compare.col.differentiator')}</span>
                </th>
                <th scope="col" className="px-4 py-2 font-semibold text-[var(--color-primary-text)] whitespace-nowrap">
                  {t('website.home.compare.col.tmos')}
                </th>
                <th scope="col" className="px-4 py-2 font-semibold text-[var(--color-tab-inactive-text)] whitespace-nowrap">
                  {t('website.home.compare.col.others')}
                </th>
              </tr>
            </thead>
            <tbody>
              {differentiatorRows.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="px-4 py-2 font-medium text-[var(--color-surface-text)] whitespace-nowrap">
                    {row.label}
                  </th>
                  <td className="px-4 py-2">
                    <CheckIcon
                      className="mx-auto h-4 w-4 text-[var(--color-primary-text)]"
                      aria-label={t('website.home.compare.tmos.yes')}
                    />
                  </td>
                  <td className="px-4 py-2 text-[var(--color-tab-inactive-text)] whitespace-nowrap">
                    {row.others}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
